import { Lock } from "lucide-react";
import { ComponentSpinner } from "@components/spinner";
import StatusChip from "@components/ui-components/status-chip";
import KycBanner from "../components/kyc-banner";
import usePublisherRouteGuard from "../use-publisher-route-guard";
import usePublisherOverview from "./use-publisher-overview";
import { formatCurrency } from "@src/utility/helpers/utils";
import { KYC_LABEL, KYC_TONE, PAYOUT_LABEL, PAYOUT_TONE } from "@src/utility/constants/panel/common";
import { formatDate } from "@src/utility/helpers/date";

const PublisherOverview = () => {
    const { publisherId } = usePublisherRouteGuard();
    const { loading, currency, publisher, brands, paymentHistory } = usePublisherOverview(publisherId);
    const verified = publisher.kyc_status === "verified";
    if (loading) {
        return (
            <div className="flex justify-center py-24">
                <ComponentSpinner />
            </div>
        );
    }

    const lastPayment = publisher.last_payment;

    return (
        <div className="p-6">
            <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>

            <KycBanner className="mt-4" />

            {/* "Total earnings (current period)" was removed — the backend currently derives it
                from the same figure as "Next expected payout" (both read stats.outstanding),
                so showing both duplicated one number under two labels. Restore it once the
                backend tracks a genuinely distinct current-period-earnings figure. */}
            <div className="mt-6 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Last payment received</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                        {lastPayment ? formatCurrency(lastPayment.amount, currency) : "—"}
                    </p>
                    {lastPayment && (
                        <p className="text-xs text-muted">{formatDate(lastPayment.paid_at)} · {lastPayment.payment_reference}</p>
                    )}
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Next expected payout</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                        {verified ? formatCurrency(publisher.next_expected_payout, currency) : "—"}
                    </p>
                    <p className="text-xs text-muted">{verified ? "On next cycle" : "Complete KYC to unlock"}</p>
                </div>
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Earnings by brand</h3>
                {brands.map((brand) => (
                    <div key={brand.id} className="rounded-md border border-gray-200 p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="font-medium text-gray-900">{brand.name}</div>
                                <div className="text-xs text-muted">{brand.platform}</div>
                            </div>
                            <div className="flex items-center gap-3">
                                <StatusChip tone={KYC_TONE[brand.kyc_status] || "neutral"} label={KYC_LABEL[brand.kyc_status] || brand.kyc_status} />
                                <StatusChip tone={PAYOUT_TONE[brand.payout_status] || "neutral"} label={PAYOUT_LABEL[brand.payout_status] || brand.payout_status} />
                                {verified ? (
                                    <div className="text-lg font-semibold text-gray-900">{formatCurrency(brand.earnings_pending, brand.currency || currency)}</div>
                                ) : (
                                    <div className="flex items-center gap-1.5 rounded-md border border-warning/30 bg-warning/10 px-2 py-1 text-xs text-warning-foreground">
                                        <Lock className="h-3.5 w-3.5" />
                                        Complete your KYC to unlock your payouts
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                {brands.length === 0 && (
                    <p className="p-6 text-center text-sm text-muted">No brand mappings yet.</p>
                )}
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 p-4 text-sm font-semibold text-gray-900">Payment history</div>
                <table className="w-full text-sm">
                    <thead className="bg-light text-left text-xs uppercase tracking-wide text-muted">
                        <tr>
                            <th className="p-3">Period</th>
                            <th className="p-3">Brand</th>
                            <th className="p-3">Amount</th>
                            <th className="p-3">Status</th>
                            <th className="p-3">Confirmation ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paymentHistory.map((entry) => (
                            <tr key={entry.id} className="border-t border-gray-100">
                                <td className="p-3 text-gray-700">{entry.period}</td>
                                <td className="p-3 text-gray-700">{entry.brand_name}</td>
                                <td className="p-3 text-gray-900">{formatCurrency(entry.amount, currency)}</td>
                                <td className="p-3"><StatusChip tone={PAYOUT_TONE[entry.status] || "neutral"} label={PAYOUT_LABEL[entry.status] || entry.status} /></td>
                                <td className="p-3 text-xs text-muted">{entry.confirmation_id || "—"}</td>
                            </tr>
                        ))}
                        {paymentHistory.length === 0 && (
                            <tr><td colSpan={5} className="p-6 text-center text-sm text-muted">No payment history yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PublisherOverview;
